import React, { useEffect, useState } from "react";
import type { Actor } from "@guildmaster/core/platform";
import {
  canBrowseIntroductions,
  introductionChoices,
  introductionFields,
  introductionLogLinks,
  introductionSpecChoices,
  type MemberIntroduction,
} from "@guildmaster/core/member-introduction";
import { api, ApiError } from "./api";
import "./members.css";

interface Directory {
  canBrowse: boolean;
  total: number;
  offset: number;
  profiles: MemberIntroduction[];
}
export function Members({
  actor,
  stage,
  onDenied,
}: {
  actor: Actor;
  stage: string;
  onDenied: (error: unknown) => void;
}) {
  const officer = canBrowseIntroductions(actor);
  const [filters, setFilters] = useState({
    q: "",
    className: "",
    spec: "",
    raidStyle: "",
    casualTeam: "",
    leadership: "",
    test: stage === "staging" ? "1" : "0",
  });
  const [offset, setOffset] = useState(0),
    [refresh, setRefresh] = useState(0);
  const [data, setData] = useState<Directory | null>(null),
    [error, setError] = useState(""),
    [loading, setLoading] = useState(true);
  useEffect(() => {
    const abort = new AbortController();
    setLoading(true);
    setError("");
    setData(null);
    const timer = window.setTimeout(() => {
      void api<Directory>(
        "/api/introductions?" +
          new URLSearchParams({ ...filters, offset: String(offset) }),
        { signal: abort.signal },
      )
        .then((value) => {
          if (!abort.signal.aborted) setData(value);
        })
        .catch((problem) => {
          if (abort.signal.aborted) return;
          if (
            problem instanceof ApiError &&
            [401, 403].includes(problem.status)
          )
            onDenied(problem);
          else
            setError(
              problem instanceof Error
                ? problem.message
                : "Could not load introductions.",
            );
        })
        .finally(() => {
          if (!abort.signal.aborted) setLoading(false);
        });
    }, 200);
    return () => {
      clearTimeout(timer);
      abort.abort();
    };
  }, [filters, offset, refresh, actor.userId, onDenied]);
  const change = (key: keyof typeof filters, value: string) => {
    setOffset(0);
    setFilters((previous) => ({
      ...previous,
      [key]: value,
      ...(key === "className" ? { spec: "" } : {}),
    }));
  };
  const specOptions = filters.className
    ? introductionSpecChoices(
        filters.className as MemberIntroduction["answers"]["className"],
      )
    : introductionChoices.spec;
  return (
    <section className="introductions" aria-label="Member introductions">
      <div className="intro-banner">
        <div>
          <span className="eyebrow">
            {officer ? "OFFICER DIRECTORY" : "YOUR INTRODUCTION"} | PRE-LAUNCH
          </span>
          <h2>Member introductions</h2>
          <p>
            {officer
              ? "Search member plans and preferences."
              : "Your plans and preferences."}{" "}
            Only you and current guild officers can see your profile.
          </p>
        </div>
        <div className="intro-how">
          <strong>Update in Discord</strong>
          <span>
            Use <code>/introduce</code> to add, edit or remove your profile.
            Changes sync here.
          </span>
        </div>
      </div>
      {officer && (
        <div className="intro-filters">
          <label className="intro-search">
            Search introductions
            <input
              type="search"
              value={filters.q}
              maxLength={200}
              placeholder="Name, experience, past character, logs…"
              onChange={(event) => change("q", event.target.value)}
            />
          </label>
          {(
            [
              ["className", "Class", "All classes"],
              ["spec", "Spec", "All specs"],
              ["raidStyle", "Raid style", "All raid styles"],
              ["casualTeam", "Casual team", "All preferences"],
              ["leadership", "Leading", "All interests"],
            ] as const
          ).map(([key, label, all]) => (
            <label key={key}>
              {label}
              {["className", "spec"].includes(key) ? " | Either choice" : ""}
              <select
                value={filters[key]}
                title={filters[key] || all}
                onChange={(event) => change(key, event.target.value)}
              >
                <option value="">{all}</option>
                {(key === "spec" ? specOptions : introductionChoices[key]).map(
                  (value) => (
                    <option key={value}>{value}</option>
                  ),
                )}
              </select>
            </label>
          ))}
        </div>
      )}
      <div className="intro-results-bar">
        <span role="status">
          {loading
            ? "Loading introductions…"
            : `${data?.total ?? 0} ${filters.test === "1" ? "test " : ""}${data?.total === 1 ? "profile" : "profiles"}${officer ? " found" : " saved"}`}
        </span>
        <div>
          {stage !== "production" && (
            <label className="intro-test">
              <input
                type="checkbox"
                checked={filters.test === "1"}
                onChange={(event) =>
                  change("test", event.target.checked ? "1" : "0")
                }
              />
              Test profiles
            </label>
          )}
          <button
            className="button secondary"
            onClick={() => setRefresh((value) => value + 1)}
          >
            Refresh
          </button>
        </div>
      </div>
      {error && (
        <p className="notice error" role="alert">
          {error}
        </p>
      )}
      {!loading && !error && !data?.profiles.length && (
        <div className="empty">
          <h3>
            {filters.q ||
            Object.entries(filters).some(
              ([key, value]) => key !== "test" && value,
            )
              ? "No introductions match"
              : "No introductions yet"}
          </h3>
          <p>
            {officer
              ? "Try another search or add an introduction in Discord."
              : "Use /introduce in Discord, then refresh."}
          </p>
        </div>
      )}
      <div className="intro-list">
        {data?.profiles.map((profile) => (
          <IntroductionCard
            key={`${profile.userId}:${profile.test}:${profile.revision}`}
            profile={profile}
            own={profile.userId === actor.userId}
          />
        ))}
      </div>
      {data && data.total > 50 && (
        <div className="intro-results-bar">
          <button
            className="button secondary"
            disabled={offset === 0}
            onClick={() => setOffset((value) => Math.max(0, value - 50))}
          >
            Previous
          </button>
          <span>
            {offset + 1}-{Math.min(offset + 50, data.total)} of {data.total}
          </span>
          <button
            className="button secondary"
            disabled={offset + 50 >= data.total}
            onClick={() => setOffset((value) => value + 50)}
          >
            Next
          </button>
        </div>
      )}
      <p className="intro-footnote">
        Self-reported plans. Mains are tentative; logs are historical. Profiles
        do not verify ownership, reserve raid places or grade performance.
      </p>
    </section>
  );
}
function IntroductionCard({
  profile,
  own,
}: {
  profile: MemberIntroduction;
  own: boolean;
}) {
  const a = profile.answers;
  const preference = (className: string, spec: string) =>
    className === "Undecided"
      ? "Undecided"
      : `${className} | ${spec === "Undecided" ? "Spec undecided" : spec}`;
  return (
    <article className="intro-card">
      <header>
        <div className="intro-avatar" aria-hidden="true">
          {(a.plannedMain || profile.displayName).slice(0, 2).toUpperCase()}
        </div>
        <div className="intro-identity">
          <span className="eyebrow">
            {profile.test ? "TEST INTRODUCTION" : "MEMBER INTRODUCTION"}
            {own ? " | YOU" : ""}
          </span>
          <h3>{a.plannedMain || "Main undecided"}</h3>
          <p>
            {profile.displayName} <span>| Tentative main</span>
          </p>
        </div>
        <span className="intro-updated">
          Updated{" "}
          {new Date(profile.updatedAt).toLocaleDateString(undefined, {
            day: "numeric",
            month: "short",
          })}
        </span>
      </header>
      <div className="intro-tags">
        <span>1st choice | {preference(a.className, a.spec)}</span>
        <span>2nd choice | {preference(a.secondClassName, a.secondSpec)}</span>
        <span>
          {a.raidStyle === "Undecided" ? "Raid style undecided" : a.raidStyle}
        </span>
        {["Yes", "Open to learning", "Maybe"].includes(a.leadership) && (
          <span className="intro-leading">Leading | {a.leadership}</span>
        )}
      </div>
      <div className="intro-team-fit">
        <strong>Open to a more casual raid team</strong>
        <span>{a.casualTeam}</span>
      </div>
      <div className="intro-highlights">
        <div>
          <h4>AVAILABILITY</h4>
          <p>{a.availability || "Not provided"}</p>
          <small>{a.timezone || "Time zone not provided"}</small>
        </div>
        <div>
          <h4>WOW EXPERIENCE</h4>
          <p>{a.experience || "Not provided"}</p>
        </div>
        <div>
          <h4>LOOKING FOR</h4>
          <p>{a.goals || "Not provided"}</p>
        </div>
      </div>
      <details>
        <summary>
          Full introduction <span>Logs, leadership and all answers</span>
        </summary>
        <dl>
          {introductionFields.map(({ key, label }) => (
            <div key={key}>
              <dt>{label}</dt>
              <dd>
                {a[key] || "Not provided"}
                {key === "logs" &&
                  introductionLogLinks(a.logs).map((url, index) => (
                    <a
                      className="intro-log"
                      key={url}
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Warcraft Logs {index + 1}
                    </a>
                  ))}
              </dd>
            </div>
          ))}
        </dl>
        <p className="intro-source">
          Submitted in Discord |{" "}
          {new Date(profile.submittedAt).toLocaleString()} | Revision{" "}
          {profile.revision}
          <br />
          Discord account ID: {profile.userId}
        </p>
      </details>
    </article>
  );
}
